import * as React from 'react';
import {
  Component,
  Fragment,
  type ErrorInfo,
  type JSX,
  type PropsWithChildren,
  type ReactNode,
} from 'react';
import * as Sentry from '@sentry/react';

import FatalErrorPage from '../FatalErrorPage/FatalErrorPage';

interface ErrorBoundaryProps {
  component?: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export default class ErrorBoundary extends Component<
  PropsWithChildren<ErrorBoundaryProps>,
  ErrorBoundaryState
> {
  declare state: ErrorBoundaryState;
  declare props: Readonly<PropsWithChildren<ErrorBoundaryProps>>;
  declare setState: <K extends keyof ErrorBoundaryState>(
    state: ((prevState: Readonly<ErrorBoundaryState>, props: Readonly<PropsWithChildren<ErrorBoundaryProps>>) => (Pick<ErrorBoundaryState, K> | ErrorBoundaryState | null)) | Pick<ErrorBoundaryState, K> | ErrorBoundaryState | null,
    callback?: () => void,
  ) => void;

  public constructor(props: PropsWithChildren<ErrorBoundaryProps>) {
    super(props);
    this.state = {
      error: null,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    Sentry.withScope((scope) => {
      scope.setExtras({ componentStack: errorInfo.componentStack });
      Sentry.captureException(error);
    });
    this.setState({
      error,
    });
  }

  public render(): JSX.Element {
    const { error } = this.state;
    const { children, component = <FatalErrorPage /> } = this.props;

    if (error) {
      return <Fragment>{component}</Fragment>;
    }

    return <Fragment>{children}</Fragment>;
  }
}
